import ComposedChart, {
  ChartDataPoint,
  ChartSeries,
  TooltipContentType,
} from '@/components/Chart/ComposedChart';
import { Card } from '@/components/ui/card';
import { CHART_BAR_COLOR } from '@/constants/chart';
import { TopMarket } from '@/service/type';
import { formatCurrencyAxis, formatCurrencyFull } from '@/utils/numberUtil';
import { formatTooltipDate } from '@/utils/timeUtils';
import { BarChart3 } from 'lucide-react';
import React, { useMemo, useState } from 'react';

interface OpenInterestDataPoint {
  date: string;
  oiNotionalUsd: string;
  topMarkets?: TopMarket[];
}

interface OpenInterestChartProps {
  data?: OpenInterestDataPoint[];
  height?: number;
}

/**
 * Custom Tooltip for Open Interest Chart
 */
const OpenInterestTooltip: TooltipContentType = ({ active, payload }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0]?.payload;
  if (!data) return null;

  const oi = data.oi as number;
  const topMarkets = (data.topMarkets || []) as TopMarket[];

  return (
    <div className="bg-solid-toast-tips rounded-lg p-3 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]">
      {/* Date */}
      <div className="text-xs font-semibold text-text-black leading-3 mb-3">
        {formatTooltipDate(data.date)}
      </div>

      {/* Daily Notional Value */}
      <div className="flex items-center text-xs text-text-black leading-3">
        <span>Daily Notional Value of OI (USDC): {formatCurrencyFull(oi)}</span>
      </div>

      {/* Divider and Top Markets */}
      {topMarkets.length > 0 && (
        <>
          <div className="border-t border-border-light-line-1 my-3" />

          {/* Top Markets */}
          <div className="flex flex-col gap-2 text-xs leading-3">
            <div className="text-text-black">Top Markets:</div>
            {topMarkets.slice(0, 3).map((market, index) => (
              <div key={index} className="text-text-main-body">
                {market.market}: {formatCurrencyFull(parseFloat(market.oiNotionalUsd))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Open Interest Chart Component
 * Displays daily open interest (bar only)
 */
const OpenInterestChart: React.FC<OpenInterestChartProps> = ({ data, height = 232 }) => {
  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>({
    oi: true,
  });

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(item => ({
      date: item.date,
      oi: parseFloat(item.oiNotionalUsd),
      topMarkets: item.topMarkets || [],
    })) as ChartDataPoint[];
  }, [data]);

  const allSeries: ChartSeries[] = [
    { dataKey: 'oi', name: 'Open Interest', type: 'bar', color: CHART_BAR_COLOR },
  ];

  const series = allSeries.filter(s => visibleSeries[s.dataKey]);

  const legends = [{ dataKey: 'oi', label: 'Open Interest', color: CHART_BAR_COLOR }];

  const toggleSeries = (dataKey: string) => {
    setVisibleSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  const hasData = chartData.length > 0;

  return (
    <Card className="overflow-hidden bg-solid-white-card">
      {/* Header */}
      <div className="p-6 pb-4">
        <h3 className="text-base font-semibold text-text-black h-6 my-0">Open Interest</h3>
      </div>

      {/* Chart Area */}
      <div className="px-6 pb-4">
        {hasData ? (
          <ComposedChart
            data={chartData}
            series={series}
            height={height}
            yAxisFormatter={formatCurrencyAxis}
            customTooltip={OpenInterestTooltip}
          />
        ) : (
          <div
            className="flex items-center justify-center border-2 border-dashed border-border-light-line-1 rounded-lg bg-solid-solid2"
            style={{ height: `${height}px` }}
          >
            <div className="text-center">
              <BarChart3 className="h-12 w-12 text-icon-grey mx-auto mb-3" />
              <p className="text-sm text-text-main-body">No Data Available</p>
              <p className="text-xs text-text-prompt mt-1">Open Interest</p>
            </div>
          </div>
        )}
      </div>

      {/* Legends */}
      <div className="px-6 pb-4 flex items-center justify-center gap-6">
        {legends.map((legend, index) => (
          <div
            key={index}
            className="flex items-center gap-2 cursor-pointer select-none transition-opacity hover:opacity-80"
            onClick={() => toggleSeries(legend.dataKey)}
            style={{ opacity: visibleSeries[legend.dataKey] ? 1 : 0.4 }}
          >
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: legend.color }} />
            <span className="text-sm text-text-main-body">{legend.label}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default OpenInterestChart;
